import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../FullResultsPage/FullResultsPage.module.css";

import ArrowRightIcon from "../../../../assets/icons/arrowTeamIcon.svg";
import {
  TableComponent,
  ActionsContainer,
} from "@react-solutions-ui/findmore-ui";
import BreadcrumbDropdown from "../../../reports/components/Banner/BreadcrumbDropdown";
import { Breadcrumb } from "antd";
import ReportIcon from "../../../../assets/icons/reportIcon.svg";

type Evaluation = {
  id: string;
  year: number;
  endDate: string;
  submitDate?: string;
  finalEvaluation?: string;
  statusReasonType?: string;
  evaluatorStatus?: number;
  evaluatorId?: string;
};

type LocationState = {
  reports: Evaluation[];
  breadcrumbLabel: string;
  employeeName: string;
  employeeArea: string;
  photo: string;
  employeeId: string;
};

export default function FullReportsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | undefined;

  const [isMobile, setIsMobile] = useState(false);
  const [selectedYear] = useState("Todos");

  useEffect(() => {
    if (!state) {
      navigate("/all-employees");
    }
  }, [state, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!state) return null;

  const filteredReports =
    selectedYear === "Todos"
      ? state.reports
      : state.reports.filter((e) => e.year === Number(selectedYear));

  const reportColumns = [
    {
      title: "Ano",
      dataIndex: "year",
      key: "year",
      onCell: () => ({ "data-label": "Ano" } as any),
      render: (text: any, record: Evaluation) => (
        <div className={styles.rowWithActions}>
          <span className={styles.authorText}>{text}</span>
          <ActionsContainer mobile={true}>
            <a href={`/report/${record.id}`}>
              <img
                src={ReportIcon}
                alt="ver"
                className={styles.mobileActionIcon}
              />
            </a>
          </ActionsContainer>
        </div>
      ),
    },
    {
      title: "Nota final",
      key: "finalEvaluation",
      onCell: () => ({ "data-label": "Nota final" } as any),
      render: (row: Evaluation) => row.finalEvaluation?.trim() || "-",
    },
    {
      title: "Data de submissão",
      key: "submitDate",
      onCell: () => ({ "data-label": "Data de submissão" } as any),
      render: (row: Evaluation) =>
        row.statusReasonType === "Completed" && row.submitDate
          ? new Date(row.submitDate).toLocaleDateString("pt-PT")
          : "-",
    },
    {
      title: "Estado",
      key: "statusReasonType",
      onCell: () => ({ "data-label": "Estado" } as any),
      render: (_: any, row: Evaluation) => {
        let value = row.statusReasonType;
        let className = "";

        switch (value) {
          case "Not Started":
            className = styles.statusNotStarted;
            break;
          case "In Progress":
            if (row.evaluatorStatus === 122470002) {
              value = "In Evaluation";
              className = styles.statusInEvaluation;
            } else if (row.evaluatorStatus === 122470001) {
              value = "Evaluated";
              className = styles.statusEvaluated;
            } else {
              className = styles.statusInProgress;
            }
            break;
          case "Completed":
            className = styles.statusCompleted;
            break;
          default:
            className = styles.statusNotStarted;
        }

        const labelMap: Record<string, string> = {
          "Not Started": "POR INICIAR",
          "In Progress": "POR AVALIAR",
          Completed: "CONCLUÍDO",
          "In Evaluation": "EM AVALIAÇÃO",
          Evaluated: "AVALIADO",
        };

        return (
          <span className={`${styles.statusBadge} ${className}`}>
            {labelMap[value ?? ""]}
          </span>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      className: "hide-on-mobile",
      onCell: () => ({ "data-label": "Ações" } as any),
      render: (_: any, record: Evaluation) => (
        <ActionsContainer>
          <button
            className={styles.evaluateButton}
            disabled={
              record.statusReasonType === "Not Started" ||
              (record.statusReasonType === "In Progress" &&
                record.evaluatorStatus === 122470001)
            }
            onClick={() => {
              if (
                record.evaluatorStatus === 1 &&
                record.statusReasonType === "In Progress"
              ) {
                navigate(`/questionnaire?id=${record.evaluatorId}`);
              } else {
                navigate(`/report/${record.id}`);
              }
            }}
          >
            {record.evaluatorStatus === 1 &&
            record.statusReasonType === "In Progress"
              ? "Avaliar"
              : "Ver relatório"}
          </button>
        </ActionsContainer>
      ),
    },
  ];

  const hiddenItems = [
    {
      label: state.breadcrumbLabel,
      href:
        state.breadcrumbLabel === "A minha equipa"
          ? "/my-team"
          : "/all-employees",
    },
    {
      label: state.employeeName,
      href: `/profile-team/${state.employeeId}`,
    },
  ];

  const breadcrumbItems = isMobile
    ? [
        {
          title: (
            <BreadcrumbDropdown
              trigger={<span className={styles.ellipsisButton}>...</span>}
              items={hiddenItems}
            />
          ),
        },
        { title: <b>Relatórios</b> },
      ]
    : [
        {
          title: (
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate(
                  state.breadcrumbLabel === "A minha equipa"
                    ? "/my-team"
                    : "/all-employees"
                );
              }}
            >
              {state.breadcrumbLabel}
            </a>
          ),
        },
        {
          title: (
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate(`/profile-team/${state.employeeId}`);
              }}
            >
              {state.employeeName}
            </a>
          ),
        },
        {
          title: <b>Relatórios</b>,
        },
      ];

  return (
    <>
      <div className={styles.blueProfile}>
        <div className="page-container">
          <Breadcrumb
            className={styles.breadcrumb}
            separator={<img src={ArrowRightIcon} alt="arrow" />}
            items={breadcrumbItems}
          />

          <div className={styles.userInfo}>
            <div className={styles.avatarCircle}>
              {state.photo ? (
                <img
                  src={`data:image/jpeg;base64,${atob(state.photo)}`}
                  alt="Avatar"
                />
              ) : (
                state.employeeName
                  .split(" ")
                  .map((p) => p[0])
                  .join("")
                  .toUpperCase()
              )}
            </div>

            <div className={styles.userText}>
              <h2 className={styles.userName}>{state.employeeName}</h2>
              <p className={styles.areaEmployee}>{state.employeeArea}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles.profileContainer} page-container`}>
        <div className={styles.profileBox}>
          <div className={styles.notesBox}>
            <div className={styles.textBox}>
              <span className={styles.titleHistory}>RELATÓRIOS</span>
              <span className={styles.smallText}>
                Consulta os relatórios de desempenho do colaborador
              </span>
            </div>
          </div>

          <TableComponent
            columns={reportColumns}
            data={filteredReports}
            pageSize={9}
          />
        </div>
      </div>
    </>
  );
}
